import { createApi } from '@reduxjs/toolkit/query/react';
import {
	collection,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	Timestamp,
	where,
} from 'firebase/firestore';
import { chunk } from 'lodash';
import { firestore } from '@/utils/firebase';
import { AUDIT_ACTIONS_COLLECTION } from '@/features/auditActions/auditActions';

// Deal/client timeline events (separate from profile auditActions).
export const ACTIVITIES_COLLECTION = 'activities';

// Combined filter options for both auditActions.actionType and activities.activityType.
export const AUDIT_ACTION_TYPES = [
	'userUpdated',
	'taskCreated',
	'taskUpdated',
	'taskDeleted',
	'noteCreated',
	'noteDeleted',
	'newClaimCreated',
	'newBusinessCreated',
	'newEndorsementCreated',
	'newRenewalCreated',
	'newDebtorCreated',
	'newProspectCreated',
	'dealArchived',
	'dealUnarchived',
	'dealDeleted',
	'clientNameChange',
	'ClaimClosed',
	'ClaimReopened',
	'prospectToClient',
	'clientToProspect',
];

const parseBoundary = (value, endOfDay = false) => {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	if (endOfDay) {
		date.setHours(23, 59, 59, 999);
	} else {
		date.setHours(0, 0, 0, 0);
	}
	return Timestamp.fromDate(date);
};

// Normalize a user/{id} doc into the performedBy shape used by the Audit table.
const mapUserSnapshot = (userSnap) => {
	if (!userSnap?.exists?.()) return null;
	const data = userSnap.data();
	return {
		userId: userSnap.id,
		hubspotId: data.hubspotId ?? null,
		name: data.name ?? null,
		email: data.email ?? null,
	};
};

// Activities store completedBy as a DocumentReference — fetch each unique user once.
const resolveCompletedByUsers = async (activityDocs) => {
	const refByPath = new Map();

	for (const docSnap of activityDocs) {
		const completedBy = docSnap.data()?.completedBy;
		if (completedBy?.path && !refByPath.has(completedBy.path)) {
			refByPath.set(completedBy.path, completedBy);
		}
	}

	const userByPath = new Map();

	await Promise.all(
		[...refByPath.entries()].map(async ([path, ref]) => {
			try {
				const userSnap = await getDoc(ref);
				const user = mapUserSnapshot(userSnap);
				if (user) {
					userByPath.set(path, user);
				}
			} catch (error) {
				console.error('[resolveCompletedByUsers]', path, error);
			}
		}),
	);

	return userByPath;
};

const mapAuditDoc = (docSnap) => {
	const data = docSnap.data();
	return {
		id: docSnap.id,
		source: 'auditAction',
		timestamp: data.timestamp?.toDate?.()?.toISOString?.() ?? null,
		actionType: data.actionType ?? null,
		// Profile rows have no client/deal; task events store clientId and dealId.
		fieldChanged: data.fieldChanged ?? null,
		previousValue: data.previousValue ?? null,
		newValue: data.newValue ?? null,
		performedBy: data.performedBy ?? null,
		targetUser: data.targetUser ?? null,
		clientId: data.clientId ?? null,
		clientName: null,
		dealId: data.dealId ?? null,
		dealName: null,
	};
};

// Map activities into the same row shape as auditActions for one shared table.
const mapActivityDoc = (docSnap, userByPath) => {
	const data = docSnap.data();
	const completedByPath = data.completedBy?.path;

	return {
		id: `activity-${docSnap.id}`, // Prefix avoids ID collisions with auditActions.
		source: 'activity',
		timestamp: data.createdDate?.toDate?.()?.toISOString?.() ?? null,
		actionType: data.activityType ?? null,
		fieldChanged: null,
		previousValue: null,
		newValue: null,
		performedBy: completedByPath ? (userByPath.get(completedByPath) ?? null) : null,
		targetUser: null,
		clientId: data.clientId != null ? String(data.clientId) : null,
		clientName: null,
		dealId: data.dealId != null ? String(data.dealId) : null,
		dealName: null,
	};
};

const sortRowsByTimestamp = (rows) =>
	[...rows].sort((a, b) => {
		const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
		const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
		return bTime - aTime;
	});

// Admin changes (written by the admin API with source 'folioAdmin') and logs marked
// adminOnly are only for the admin audit log, not this table.
const isCrmAuditDoc = (docSnap) => {
	const { source, adminOnly } = docSnap.data();
	return source !== 'folioAdmin' && adminOnly !== true;
};

const filterRows = ({ rows, userId, userIds = [], actionTypes }) => {
	let filtered = rows;

	const resolvedUserIds = userIds.length > 0 ? userIds : userId != null ? [userId] : [];

	if (resolvedUserIds.length) {
		const allowed = new Set(resolvedUserIds.map(String));
		filtered = filtered.filter((row) => allowed.has(String(row.performedBy?.userId)));
	}

	if (actionTypes?.length) {
		const allowedTypes = new Set(actionTypes);
		filtered = filtered.filter((row) => allowedTypes.has(row.actionType));
	}

	return filtered;
};

export const auditApi = createApi({
	reducerPath: 'auditApi',
	tagTypes: ['AUDIT_ACTIONS'],
	endpoints: (builder) => ({
		searchAuditActions: builder.query({
			queryFn: async ({
				dateFrom,
				dateTo,
				userIds = [],
				actionTypes = [],
				userId,
				clientId,
			}) => {
				try {
					const from = parseBoundary(dateFrom, false);
					const to = parseBoundary(dateTo, true);

					if (!from || !to) {
						return { error: { status: 400, data: 'dateFrom and dateTo are required' } };
					}

					const resolvedUserIds =
						userIds.length > 0 ? userIds : userId != null ? [userId] : [];
					const userIdChunks = chunk(resolvedUserIds, 30);
					const clientConstraints = clientId ? [where('clientId', '==', clientId)] : [];

					// Query both collections independently by their respective date fields.
					const auditQueries = userIdChunks.map((userIdChunk) =>
						query(
							collection(firestore, AUDIT_ACTIONS_COLLECTION),
							where('performedBy.userId', 'in', userIdChunk),
							...clientConstraints,
							where('timestamp', '>=', from),
							where('timestamp', '<=', to),
							orderBy('timestamp', 'desc'),
							limit(500),
						),
					);

					const activitiesQueries = userIdChunks.map((userIdChunk) =>
						query(
							collection(firestore, ACTIVITIES_COLLECTION),
							where(
								'completedBy',
								'in',
								userIdChunk.map((id) => doc(firestore, 'user', id)),
							),
							...clientConstraints,
							where('createdDate', '>=', from),
							where('createdDate', '<=', to),
							orderBy('createdDate', 'desc'),
							limit(500),
						),
					);

					const [auditSnapshots, activitiesSnapshots] = await Promise.all([
						Promise.all(auditQueries.map((auditQuery) => getDocs(auditQuery))),
						Promise.all(
							activitiesQueries.map((activitiesQuery) => getDocs(activitiesQuery)),
						),
					]);

					const activityDocs = activitiesSnapshots.flatMap((snapshot) => snapshot.docs);
					const userByPath = await resolveCompletedByUsers(activityDocs);

					const auditRows = auditSnapshots.flatMap((snapshot) =>
						snapshot.docs.filter(isCrmAuditDoc).map(mapAuditDoc),
					);
					const activityRows = activityDocs.map((docSnap) =>
						mapActivityDoc(docSnap, userByPath),
					);

					// Merge, sort, then apply shared user/type filters.
					const mergedRows = sortRowsByTimestamp([...auditRows, ...activityRows]);
					const data = filterRows({
						rows: mergedRows,
						userId,
						userIds,
						actionTypes,
					});

					const hasMore = [...auditSnapshots, ...activitiesSnapshots].some(
						(snapshot) => snapshot.docs.length >= 500,
					);

					return { data: { data, hasMore } };
				} catch (error) {
					console.error('[searchAuditActions]', error);
					return { error: { status: 500, data: error.message } };
				}
			},
			providesTags: ['AUDIT_ACTIONS'],
		}),
	}),
});

export const { useSearchAuditActionsQuery, useLazySearchAuditActionsQuery } = auditApi;
